import React, { useMemo } from 'react';

import { Flex } from '@raystack/apsara';

import { ActionStep, Citations, StepWithID } from '@src/clients/seeker/api';
import { InfoComp } from '@src/components/CoPilot/ResponseLinks/InfoComp';
import { TOOL_TYPES } from '@src/utils/constants/COPILOT_CONSTANTS';

interface CitationGroupProps {
  currentIndex: number;
  steps: StepWithID[];
}

const CitationGroup: React.FC<CitationGroupProps> = ({
  currentIndex,
  steps
}) => {
  const groupedCitations = useMemo(() => {
    const grouped: Array<{ url: string; text: string; score: number }> = [];
    const processedIndices = new Set<number>();

    // Traverse subsequent steps to group consecutive InformationRetrieval steps
    for (let i = currentIndex; i < steps.length; i++) {
      if (processedIndices.has(i)) continue;

      const step = steps[i]?.step as ActionStep;
      if (step?.name === TOOL_TYPES.INFORMATION) {
        const toolData = step.tool_data as Citations;
        const citations =
          toolData?.citations?.map(citation => ({
            url: citation.url,
            text: new URL(citation.url).hostname,
            score: citation.score
          })) || [];
        grouped.push(...citations);
        processedIndices.add(i);
      } else {
        break;
      }
    }

    // Traverse previous steps to group consecutive InformationRetrieval steps
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (processedIndices.has(i)) continue;

      const step = steps[i]?.step as ActionStep;
      if (step?.name === TOOL_TYPES.INFORMATION) {
        const toolData = step.tool_data as Citations;
        const citations =
          toolData?.citations?.map(citation => ({
            url: citation.url,
            text: new URL(citation.url).hostname,
            score: citation.score
          })) || [];
        grouped.unshift(...citations);
        processedIndices.add(i);
      } else {
        break;
      }
    }

    return grouped;
  }, [currentIndex, steps]);

  return (
    <Flex direction='column' gap='small'>
      <InfoComp links={groupedCitations} />
    </Flex>
  );
};

export default CitationGroup;
